import { handleAxiosError } from '@/api/helpers/api-errors.helper';
import { HubspotErrorLog } from '@/client/hubspot';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { DialogContext } from '@/common/context';
import { useDeleteHubspotErrorLog, useHubspotErrorLogs } from '@/hooks/query';
import { useToast } from '@/hooks/useToast';
import {
  EmptyStateCard,
  EmptyStateContent,
  ErrorTable,
  PageContainer,
  TableLog,
} from '@/pages/accounts/account-errors/error-log.styled';
import { describeHubspotError } from '@/pages/hubspot-error-log/hubspot-error-log.helpers';
import { DateFormats, DialogType } from '@/system-settings/enums';
import { AppButton } from '@/ui/buttons';
import { formatDate } from '@/utils/date/date.helper';
import { Paginator } from 'primereact/paginator';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const PAGE_SIZE = 25;

export const HubspotErrorLogPage: React.FC = () => {
  const { t } = useTranslation();
  const [skip, setSkip] = useState(0);
  const toast = useToast();
  const { setDialogData } = useContext(DialogContext);
  const { errorLogs, isLoading, isError } = useHubspotErrorLogs({
    take: PAGE_SIZE,
    skip,
  });
  const { deleteErrorLog } = useDeleteHubspotErrorLog();

  const count = errorLogs?.count;

  useEffect(() => {
    if (count !== undefined && skip > 0 && skip >= count) {
      setSkip(Math.max(0, skip - PAGE_SIZE));
    }
  }, [count, skip]);

  const confirmDelete = (row: HubspotErrorLog) =>
    setDialogData({
      type: DialogType.WARNING,
      show: true,
      showDialogConfirmation: false,
      header: t('hubspotErrorLog.deleteConfirmTitle'),
      message: t('hubspotErrorLog.deleteConfirmMessage'),
      confirmButtonLabel: t('button.delete'),
      onAccept: async () => {
        try {
          await deleteErrorLog(row.id);
        } catch (e) {
          handleAxiosError(e, toast);
        }
      },
    });

  const rows = errorLogs?.result ?? [];

  return (
    <PageContainer direction="column" align="flex-start">
      <h1 className="mt-2 mb-2">{t('hubspotErrorLog.title')}</h1>
      <p className="mt-0">{t('hubspotErrorLog.intro')}</p>

      {isError && rows.length === 0 ? (
        <EmptyStateCard>
          <EmptyStateContent>
            <i className="pi pi-exclamation-triangle" />
            <span>{t('hubspotErrorLog.loadError')}</span>
          </EmptyStateContent>
        </EmptyStateCard>
      ) : !isLoading && rows.length === 0 ? (
        <EmptyStateCard>
          <EmptyStateContent>
            <i className="pi pi-check-circle" />
            <span>{t('hubspotErrorLog.empty')}</span>
          </EmptyStateContent>
        </EmptyStateCard>
      ) : (
        <TableLog>
          <ErrorTable>
            <thead>
              <tr>
                <th>{t('hubspotErrorLog.account')}</th>
                <th>{t('hubspotErrorLog.description')}</th>
                <th>{t('hubspotErrorLog.lastOccurrence')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <Link
                      to={RedirectPaths[
                        RedirectPathsEnum.ACCOUNTS_INTERNAL_HUBSPOT
                      ](row.account.id)}
                    >
                      {row.account.name}
                    </Link>
                  </td>
                  <td>{describeHubspotError(row, t)}</td>
                  <td>
                    {formatDate(row.lastOccurredAt, DateFormats.TIMEDATE)}
                  </td>
                  <td>
                    <AppButton
                      icon="pi pi-trash"
                      type="text"
                      severity="danger"
                      ariaLabel={t('hubspotErrorLog.delete')}
                      onClick={() => confirmDelete(row)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </ErrorTable>
        </TableLog>
      )}

      {(errorLogs?.count ?? 0) > PAGE_SIZE && (
        <Paginator
          first={skip}
          rows={PAGE_SIZE}
          totalRecords={errorLogs?.count}
          onPageChange={(e) => setSkip(e.first)}
        />
      )}
    </PageContainer>
  );
};
